import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  LinearProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Speed as SpeedIcon,
  Memory as MemoryIcon,
  Timer as TimerIcon
} from '@mui/icons-material';
import { useMemoryMonitor } from '../../hooks/usePerformance';

const PerformanceDashboard = ({ show = false }) => {
  const [performanceData, setPerformanceData] = useState({
    renderTime: 0,
    memoryUsage: null,
    loadTime: 0,
    componentCounts: {}
  });
  const { checkMemory } = useMemoryMonitor();

  useEffect(() => {
    if (!show) return;

    const interval = setInterval(() => {
      const memory = checkMemory();
      const timing = performance.timing;
      const loadTime = timing.loadEventEnd - timing.navigationStart;

      setPerformanceData(prev => ({
        ...prev,
        memoryUsage: memory,
        loadTime,
        renderTime: performance.now()
      }));
    }, 1000);

    return () => clearInterval(interval);
  }, [show, checkMemory]);

  if (!show) return null;

  const getMemoryStatus = (memory) => {
    if (!memory) return 'unknown';
    const usage = (memory.used / memory.limit) * 100;
    if (usage > 80) return 'high';
    if (usage > 60) return 'medium';
    return 'low';
  };

  const getMemoryColor = (status) => {
    switch (status) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  return (
    <Paper 
      sx={{ 
        position: 'fixed',
        top: 80,
        right: 20,
        width: 300,
        maxHeight: '80vh',
        overflow: 'auto',
        p: 2,
        zIndex: 1000,
        opacity: 0.95
      }}
      elevation={8}
    >
      <Typography variant="h6" gutterBottom>
        <SpeedIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Performance Monitor
      </Typography>

      <Grid container spacing={2}>
        {/* Memory Usage */}
        {performanceData.memoryUsage && (
          <Grid item xs={12}>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <MemoryIcon sx={{ mr: 1 }} />
                <Typography variant="body2">Memory Usage</Typography>
                <Chip
                  label={getMemoryStatus(performanceData.memoryUsage)}
                  size="small"
                  color={getMemoryColor(getMemoryStatus(performanceData.memoryUsage))}
                  sx={{ ml: 1 }}
                />
              </Box>
              <LinearProgress
                variant="determinate"
                value={(performanceData.memoryUsage.used / performanceData.memoryUsage.limit) * 100}
                color={getMemoryColor(getMemoryStatus(performanceData.memoryUsage))}
                sx={{ mb: 1 }}
              />
              <Typography variant="caption">
                {performanceData.memoryUsage.used}MB / {performanceData.memoryUsage.limit}MB
              </Typography>
            </Box>
          </Grid>
        )}

        {/* Load Time */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <TimerIcon sx={{ mr: 1 }} />
            <Typography variant="body2">Page Load Time</Typography>
          </Box>
          <Typography variant="h6" color="primary">
            {performanceData.loadTime}ms
          </Typography>
        </Grid>

        {/* Performance Metrics Table */}
        <Grid item xs={12}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Metric</TableCell>
                  <TableCell align="right">Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>FCP</TableCell>
                  <TableCell align="right">
                    {Math.round(performance.getEntriesByType('paint')[0]?.startTime || 0)}ms
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>LCP</TableCell>
                  <TableCell align="right">
                    {Math.round(performance.getEntriesByType('largest-contentful-paint')[0]?.startTime || 0)}ms
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>CLS</TableCell>
                  <TableCell align="right">
                    {(performance.getEntriesByType('layout-shift')[0]?.value || 0).toFixed(3)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    </Paper>
  );
};

// HOC to add performance monitoring to any component
export const withPerformanceMonitoring = (WrappedComponent) => {
  return React.forwardRef((props, ref) => {
    const [showPerformance, setShowPerformance] = useState(
      process.env.NODE_ENV === 'development' && localStorage.getItem('showPerformanceMonitor') === 'true'
    );

    useEffect(() => {
      const handleKeyPress = (e) => {
        if (e.ctrlKey && e.shiftKey && e.key === 'P') {
          const newShow = !showPerformance;
          setShowPerformance(newShow);
          localStorage.setItem('showPerformanceMonitor', newShow.toString());
        }
      };

      window.addEventListener('keydown', handleKeyPress);
      return () => window.removeEventListener('keydown', handleKeyPress);
    }, [showPerformance]);

    return (
      <>
        <WrappedComponent {...props} ref={ref} />
        <PerformanceDashboard show={showPerformance} />
        {process.env.NODE_ENV === 'development' && (
          <Box sx={{ position: 'fixed', bottom: 20, right: 20, zIndex: 1001 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={showPerformance}
                  onChange={(e) => {
                    setShowPerformance(e.target.checked);
                    localStorage.setItem('showPerformanceMonitor', e.target.checked.toString());
                  }}
                  size="small"
                />
              }
              label="Performance"
              sx={{ 
                backgroundColor: 'rgba(255,255,255,0.9)',
                borderRadius: 1,
                px: 1,
                fontSize: '0.75rem'
              }}
            />
          </Box>
        )}
      </>
    );
  });
};

export default PerformanceDashboard;
